<div class="page-head">
  <div>
    <h1 class="title">Booking reports</h1>
    <p class="desc">Booking volumes across providers, agents, applications and agencies.</p>
  </div>

  <!-- Export popover: the export spans every agency of the consumer, so it is the owner's alone. -->
  @if (isConsumerOwner) {
    <div class="export-pop relative">
      <button
        type="button"
        class="btn btn-outline"
        aria-haspopup="dialog"
        [attr.aria-expanded]="isExportDropdownOpen"
        (click)="isExportDropdownOpen = !isExportDropdownOpen"
      >
        <app-icon name="download" />
        Export
      </button>
      @if (isExportDropdownOpen) {
        <form
          class="card pop absolute right-0 top-[calc(100%+6px)] z-30 w-80 max-w-[calc(100vw-32px)] p-4"
          [formGroup]="emailForm"
          (ngSubmit)="addEmail()"
        >
          <h3 class="m-0 text-sm font-semibold">Email booking export</h3>
          <p class="m-0 mt-1 text-xs text-muted-foreground">
            Orders issued {{ selectedDateRange.startDate.format('YYYY-MM-DD') }} –
            {{ selectedDateRange.endDate.format('YYYY-MM-DD') }}.
          </p>
          <div class="mt-4 flex flex-col gap-2.5">
            <span class="label">Recipients</span>
            @for (email of recipientEmails; track email) {
              <label class="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  class="check"
                  [checked]="selectedRecipientEmails.includes(email)"
                  (change)="onPassengerEmailsChange(email)"
                />
                <span class="truncate lowercase">{{ email }}</span>
              </label>
            }
            <div class="mt-1 flex items-center gap-2">
              <input
                class="input"
                type="email"
                placeholder="Add email…"
                aria-label="Add recipient email"
                formControlName="email"
              />
              <button
                type="submit"
                class="btn btn-outline btn-icon flex-none"
                aria-label="Add recipient"
                [disabled]="emailForm.get('email')?.invalid"
              >
                <app-icon name="plus" />
              </button>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-primary btn-block mt-4"
            [disabled]="!selectedRecipientEmails.length"
            (click)="export()"
          >
            @if (selectedRecipientEmails.length) {
              Send to {{ selectedRecipientEmails.length }}
              {{ selectedRecipientEmails.length === 1 ? 'recipient' : 'recipients' }}
            } @else {
              Select a recipient
            }
          </button>
        </form>
      }
    </div>
  }
</div>

<!-- Filters -->
<div class="card mb-6">
  <div class="flex flex-wrap items-end gap-4 p-4">
    <div class="field">
      <label class="label" for="report-range">Date range</label>
      <div class="input w-[260px]">
        <app-icon name="calendar" class="text-muted-foreground" />
        <input
          id="report-range"
          type="text"
          ngxDaterangepickerMd
          [(ngModel)]="selectedDateRange"
          (datesUpdated)="onDateRangeChange($event)"
          [showCustomRangeLabel]="true"
          [locale]="{ applyLabel: 'OK', format: 'YYYY-MM-DD', separator: ' – ' }"
          [ranges]="ranges"
          [timePicker]="false"
          placeholder="Select date range"
        />
      </div>
    </div>
    <div class="field">
      <label class="label" for="report-group">Group by</label>
      <select
        id="report-group"
        class="input w-36"
        [ngModel]="selectedRange"
        (ngModelChange)="selectRange($event)"
      >
        <option value="day">Day</option>
        <option value="week">Week</option>
        <option value="month">Month</option>
        <option value="year">Year</option>
      </select>
    </div>
    <div class="field">
      <label class="label" for="report-listing">Listing by</label>
      <select
        id="report-listing"
        class="input w-40"
        [ngModel]="selectedGroup"
        (ngModelChange)="selectGroup($event)"
      >
        <option value="provider">Provider</option>
        <option value="agent">Agent</option>
        <option value="application">Application</option>
        <option value="agency">Agency</option>
      </select>
    </div>
    @if (agencies.length) {
      <div class="field">
        <label class="label" for="report-agency">
          Agency
          <span class="badge badge-outline px-1.5 py-0 text-[10px]">Superagency</span>
        </label>
        <select
          id="report-agency"
          class="input w-52"
          [ngModel]="selectedAgency?.id ?? ''"
          (ngModelChange)="selectAgencyById($event)"
        >
          <option value="">All agencies</option>
          @for (agency of agencies; track agency.id) {
            <option [value]="agency.id">{{ agency.name }}</option>
          }
        </select>
      </div>
    }
    <div class="ml-auto flex flex-wrap items-center gap-1.5">
      <span class="text-xs text-muted-foreground">Quick ranges</span>
      <button
        type="button"
        class="btn btn-sm"
        [class.btn-secondary]="activeQuickRange === '30d'"
        [class.btn-ghost]="activeQuickRange !== '30d'"
        (click)="setQuickRange('30d')"
      >
        30 days
      </button>
      <button
        type="button"
        class="btn btn-sm"
        [class.btn-secondary]="activeQuickRange === 'month'"
        [class.btn-ghost]="activeQuickRange !== 'month'"
        (click)="setQuickRange('month')"
      >
        This month
      </button>
      <button
        type="button"
        class="btn btn-sm"
        [class.btn-secondary]="activeQuickRange === '12m'"
        [class.btn-ghost]="activeQuickRange !== '12m'"
        (click)="setQuickRange('12m')"
      >
        12 months
      </button>
    </div>
  </div>
</div>

<!-- Status KPIs -->
<div
  class="mb-6 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]"
  [class.opacity-60]="loadingSummary"
>
  @for (s of statuses; track s.key) {
    <div class="card px-5 py-4">
      <div class="kpi-l flex items-center gap-2">
        <span class="dot" [style.background]="statusColors[s.label]"></span>
        {{ s.label }}
      </div>
      @if (loaded) {
        <div class="kpi-v mt-1 text-xl">{{ totals[s.key] | number }}</div>
        @if (s.key === 'orders_issued') {
          <div class="kpi-s">{{ shareOfBooked(s.key) | percent: '1.1-1' }} of booked</div>
        } @else if (s.key !== 'orders_created') {
          <div class="kpi-s">{{ shareOfBooked(s.key) | percent: '1.1-1' }}</div>
        }
      } @else {
        <div class="skeleton mt-2 h-6 w-16"></div>
      }
    </div>
  }
</div>

<div class="two-one mb-6">
  <!-- Volume over time -->
  <div class="card" [class.opacity-60]="loadingBreakdown">
    <div class="card-h">
      <h2 class="card-t">Volume over time</h2>
      <div class="flex flex-wrap items-center gap-3.5">
        @for (s of statuses; track s.key) {
          <span class="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span class="dot" [style.background]="statusColors[s.label]"></span>
            {{ s.label }}
          </span>
        }
      </div>
    </div>
    <div class="card-c">
      <div class="relative h-72 w-full">
        @if (!loaded) {
          <div class="skeleton h-full w-full"></div>
        } @else if (!lineChartData.labels?.length) {
          <div class="empty flex h-full items-center justify-center">
            No bookings in this date range.
          </div>
        } @else {
          <canvas
            baseChart
            type="line"
            [data]="lineChartData"
            [options]="lineChartOptions"
            [plugins]="chartPlugins"
            aria-label="Booking volume by status over time"
          ></canvas>
        }
      </div>
    </div>
  </div>

  <!-- Status distribution -->
  <div class="card" [class.opacity-60]="loadingSummary">
    <div class="card-h">
      <h2 class="card-t">Status distribution</h2>
      <span class="text-xs text-muted-foreground">
        of {{ totals.orders_created | number }} booked
      </span>
    </div>
    <div class="card-c flex flex-col gap-5">
      @if (!loaded) {
        <div class="skeleton h-3 w-full"></div>
        <div class="skeleton h-24 w-full"></div>
      } @else if (!totals.orders_created) {
        <p class="m-0 text-sm text-muted-foreground">No bookings in this date range.</p>
      } @else {
        <div
          class="flex h-3 gap-0.5 overflow-hidden rounded-full"
          role="img"
          [attr.aria-label]="distributionLabel"
        >
          @for (d of distribution; track d.label) {
            @if (d.share > 0) {
              <span [style.width.%]="d.share * 100" [style.background]="d.color"></span>
            }
          }
          @if (otherShare > 0) {
            <span class="bg-muted" [style.width.%]="otherShare * 100"></span>
          }
        </div>
        <div class="flex flex-col gap-3">
          @for (d of distribution; track d.label) {
            <div class="flex items-center justify-between text-sm">
              <span class="flex items-center gap-2">
                <span class="dot" [style.background]="d.color"></span>
                {{ d.label }}
              </span>
              <span>
                <b class="font-medium">{{ d.count | number }}</b>
                <span class="inline-block w-12 text-right text-muted-foreground">
                  {{ d.share | percent: '1.1-1' }}
                </span>
              </span>
            </div>
          }
          @if (otherCount > 0) {
            <div class="flex items-center justify-between text-sm">
              <span class="flex items-center gap-2 text-muted-foreground">
                <span class="dot border border-border bg-muted"></span>
                Other
              </span>
              <span>
                <b class="font-medium">{{ otherCount | number }}</b>
                <span class="inline-block w-12 text-right text-muted-foreground">
                  {{ otherShare | percent: '1.1-1' }}
                </span>
              </span>
            </div>
          }
        </div>
      }
    </div>
  </div>
</div>

<!-- Breakdown table -->
<div class="card scroll-x" [class.opacity-60]="loadingSummary">
  <div class="card-h pb-4">
    <h2 class="card-t">Breakdown by {{ listingLabel }}</h2>
    <span class="text-xs text-muted-foreground">
      @if (selectedGroup !== 'application') {
        Grouped by application ·
      }
      sorted by booked
    </span>
  </div>
  @if (!loaded) {
    <div class="flex flex-col gap-3 px-6 pb-6">
      <div class="skeleton h-5 w-full"></div>
      <div class="skeleton h-5 w-2/3"></div>
    </div>
  } @else if (!tableGroups.length) {
    <div class="empty">No data available</div>
  } @else {
    <table class="tbl">
      <thead>
        <tr>
          <th>Name</th>
          @for (s of statuses; track s.key) {
            <th class="num">{{ s.label }}</th>
          }
        </tr>
      </thead>
      <tbody>
        @for (group of tableGroups; track $index) {
          @if (group.application !== null) {
            <tr class="group">
              <td [attr.colspan]="statuses.length + 1">
                <span class="flex items-center gap-2 text-xs font-medium">
                  <app-icon name="apps" class="sm text-muted-foreground" />
                  {{ group.application }}
                </span>
              </td>
            </tr>
          }
          @for (row of group.rows; track $index) {
            <tr>
              <td>
                @if (row.code) {
                  <span class="flex items-center gap-2">
                    <span class="airline sm">{{ row.code }}</span>
                    {{ row.name }}
                  </span>
                } @else {
                  {{ row.name }}
                }
              </td>
              @for (s of statuses; track s.key) {
                <td class="num">{{ row.counts[s.key] | number }}</td>
              }
            </tr>
          }
          @if (group.total) {
            <tr class="total">
              <td>Total</td>
              @for (s of statuses; track s.key) {
                <td class="num">{{ group.total[s.key] | number }}</td>
              }
            </tr>
          }
        }
      </tbody>
    </table>
  }
</div>
